export type TransitTiming = { date: string; time: string };
export function timezoneMinutes(value: any): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (/^[+-]\d{4}$/.test(text))
    return (
      (text[0] === "-" ? -1 : 1) *
      (Number(text.slice(1, 3)) * 60 + Number(text.slice(3)))
    );
  if (/^-?\d{3,4}$/.test(text)) return Number(text);
  const match = text.match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/);
  return match
    ? (match[1] === "-" ? -1 : 1) *
        (Number(match[2]) * 60 + Number(match[3] || 0))
    : undefined;
}
export function transitTimezone(chart: any): number {
  const place = chart?.birthDetails?.placeOfBirth || {};
  return (
    timezoneMinutes(place.timezone) ??
    timezoneMinutes(place.timezoneOffset) ??
    0
  );
}
export function transitTimezoneLabel(offset: number): string {
  const absolute = Math.abs(offset);
  return `UTC${offset < 0 ? "-" : "+"}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
}
export function transitNow(offset: number, now = new Date()): TransitTiming {
  const shifted = new Date(now.getTime() + offset * 60000).toISOString();
  return { date: shifted.slice(0, 10), time: shifted.slice(11, 19) };
}
export function validTransitTiming(value: TransitTiming): boolean {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value.date) ||
    !/^\d{2}:\d{2}(?::\d{2})?$/.test(value.time)
  )
    return false;
  const parsed = new Date(
    `${value.date}T${value.time.length === 5 ? value.time + ":00" : value.time}Z`,
  );
  return (
    Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value.date
  );
}
export function stepTransit(
  value: TransitTiming,
  unit: "day" | "month",
  amount: number,
): TransitTiming {
  if (!validTransitTiming(value)) return value;
  const date = new Date(`${value.date}T12:00:00Z`);
  if (unit === "day") date.setUTCDate(date.getUTCDate() + amount);
  else {
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + amount);
    const last = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    ).getUTCDate();
    date.setUTCDate(Math.min(day, last));
  }
  return { date: date.toISOString().slice(0, 10), time: value.time };
}
const EXALT: Record<string, number> = {
  SUN: 1,
  MOON: 2,
  MARS: 10,
  MERCURY: 6,
  JUPITER: 4,
  VENUS: 12,
  SATURN: 7,
  RAHU: 3,
  KETU: 9,
};
const DEBIL: Record<string, number> = {
  SUN: 7,
  MOON: 8,
  MARS: 4,
  MERCURY: 12,
  JUPITER: 10,
  VENUS: 6,
  SATURN: 1,
  RAHU: 9,
  KETU: 3,
};
const ORB: Record<string, number> = {
  MOON: 12,
  MARS: 17,
  MERCURY: 14,
  JUPITER: 11,
  VENUS: 10,
  SATURN: 15,
};
const STATUS_NAMES: Record<string, string> = {
  E: "Exalted",
  D: "Debilitated",
  R: "Retrograde",
  C: "Combust",
  V: "Vargottama",
  A: "Own sign",
};
const normalizeToken = (value: any) => {
  const token = String(value || "").trim();
  const lower = token.toLowerCase();
  if (token === "E" || lower.includes("exalt")) return "E";
  if (token === "D" || token === "Db" || lower.includes("debil")) return "D";
  if (token === "R" || lower.includes("retro")) return "R";
  if (token === "C" || lower.includes("combust")) return "C";
  if (token === "V" || lower.includes("vargott")) return "V";
  if (token === "A" || lower.includes("own sign")) return "A";
  return undefined;
};
export function transitPlanetDetails(chart: any, key: string, planet: any) {
  const name = String(planet?.name || key).toUpperCase();
  const rawLongitude = planet?.longitude;
  const longitude =
    rawLongitude !== null &&
    rawLongitude !== undefined &&
    Number.isFinite(Number(rawLongitude))
      ? ((Number(rawLongitude) % 360) + 360) % 360
      : undefined;
  const sign =
    Number(planet?.sign?.number) ||
    (longitude !== undefined ? Math.floor(longitude / 30) + 1 : undefined);
  const statuses = new Set<string>();
  const raw = planet?.status ?? planet?.statuses;
  const tokens = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.match(/^(?:Db|[EDRCVA])+$/)
        ? raw.match(/Db|[EDRCVA]/g) || []
        : [raw]
      : [];
  for (const value of tokens) {
    const token = normalizeToken(value);
    if (token) statuses.add(token);
  }
  if (EXALT[name] === sign || planet?.isExalted) statuses.add("E");
  if (DEBIL[name] === sign || planet?.isDebilitated) statuses.add("D");
  if (
    planet?.isRetrograde ||
    planet?.retrograde ||
    (Number(planet?.speed) < 0 && !["RAHU", "KETU"].includes(name))
  )
    statuses.add("R");
  if (planet?.isCombust) statuses.add("C");
  if (planet?.isVargottama) statuses.add("V");
  if (planet?.isOwnSign) statuses.add("A");
  const sun: any = Object.entries(chart?.planets || {}).find(
    ([sunKey, item]: [string, any]) =>
      String(item?.name || sunKey).toUpperCase() === "SUN",
  )?.[1];
  if (
    longitude !== undefined &&
    sun?.longitude !== undefined &&
    sun?.longitude !== null &&
    Number.isFinite(Number(sun.longitude)) &&
    ORB[name]
  ) {
    const sunLongitude = ((Number(sun.longitude) % 360) + 360) % 360;
    const arc = Math.abs(longitude - sunLongitude);
    if (Math.min(arc, 360 - arc) < ORB[name]) statuses.add("C");
  }
  const degree =
    longitude !== undefined
      ? Math.floor(longitude % 30)
      : planet?.degree?.degrees;
  // Truncate at arcsecond precision so a degree label never rolls into the next sign.
  const seconds =
    longitude !== undefined
      ? Math.floor((longitude % 30) * 3600 + 1e-7)
      : undefined;
  const formattedDegree =
    seconds !== undefined
      ? `${Math.floor(seconds / 3600)}° ${String(Math.floor((seconds % 3600) / 60)).padStart(2, "0")}′ ${String(seconds % 60).padStart(2, "0")}″`
      : planet?.degree?.formatted || "—";
  const ordered = ["E", "D", "R", "C", "V", "A"].filter((token) =>
    statuses.has(token),
  );
  return {
    name,
    sign,
    degree,
    formattedDegree,
    status: ordered.join(""),
    attributes: ordered.map((token) => STATUS_NAMES[token]),
  };
}
